// src/admin/components/Navbar.jsx
import React, { useEffect, useRef, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { db } from "../firebase";
import {
  collection,
  query,
  where,
  orderBy,
  limit as qLimit,
  onSnapshot,
  doc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";

/* Top links you want in the navbar (kept short; sidebar has the full list) */
const topLinks = [
  { to: "/dashboard", label: "Dashboard" },
  { to: "/sales", label: "Package Sales" },
  { to: "/shop-owners-summary", label: "Shop Owners" },
  { to: "/buyer-insights", label: "Buyer Insights" },
  { to: "/affiliate-settlements", label: "Affiliate Settlements" },
];

// utils
const initials = (name = "") => {
  const parts = name.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase()).join("") || "A";
};

// UI
const Avatar = ({ name = "Admin", photoURL }) => {
  if (photoURL) {
    return (
      <img
        src={photoURL}
        alt={name}
        className="w-8 h-8 rounded-full border object-cover"
        referrerPolicy="no-referrer"
      />
    );
  }
  return (
    <div
      className="w-8 h-8 rounded-full grid place-items-center text-[11px] font-semibold
                 bg-gradient-to-tr from-indigo-500/20 via-sky-500/20 to-fuchsia-500/20
                 text-gray-700 border"
    >
      {initials(name)}
    </div>
  );
};

const Navbar = () => {
  const { pathname } = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [userOpen, setUserOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [q, setQ] = useState("");
  const [notifs, setNotifs] = useState([]);

  const userBtnRef = useRef(null);
  const notifBtnRef = useRef(null);

  const unreadCount = notifs.filter((n) => n.status !== "read").length;

  // close popovers on ESC
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") {
        setMenuOpen(false);
        setUserOpen(false);
        setNotifOpen(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // close user menu on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (!userBtnRef.current) return;
      if (!userBtnRef.current.parentElement.contains(e.target)) {
        setUserOpen(false);
      }
    };
    if (userOpen) window.addEventListener("click", onClick);
    return () => window.removeEventListener("click", onClick);
  }, [userOpen]);

  // close notif menu on outside click
  useEffect(() => {
    const onClick = (e) => {
      if (!notifBtnRef.current) return;
      if (!notifBtnRef.current.parentElement.contains(e.target)) {
        setNotifOpen(false);
      }
    };
    if (notifOpen) window.addEventListener("click", onClick);
    return () => window.removeEventListener("click", onClick);
  }, [notifOpen]);

  // live notifications for admins
  useEffect(() => {
    const qy = query(
      collection(db, "notifications"),
      where("audience", "==", "admins"),
      orderBy("createdAt", "desc"),
      qLimit(20),
    );
    const unsub = onSnapshot(
      qy,
      (snap) => {
        setNotifs(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      },
      (error) => {
        console.warn("admin notifications listener blocked", error);
        setNotifs([]);
      },
    );
    return unsub;
  }, []);

  const markNotifRead = async (id) => {
    try {
      await updateDoc(doc(db, "notifications", id), {
        status: "read",
        readAt: serverTimestamp(),
      });
    } catch (e) {
      console.warn("markNotifRead failed", e);
    }
  };

  const ActiveLink = ({ to, children }) => (
    <NavLink
      to={to}
      className={({ isActive }) =>
        [
          "px-3 py-2 rounded-lg text-sm font-medium transition-colors",
          isActive
            ? "bg-blue-600 text-white"
            : "text-gray-700 hover:bg-gray-100",
        ].join(" ")
      }
      onClick={() => setMenuOpen(false)}
    >
      {children}
    </NavLink>
  );

  return (
    <header className="sticky top-0 z-40">
      {/* subtle accent line */}
      <div className="h-[3px] bg-gradient-to-r from-indigo-500 via-fuchsia-500 to-pink-500" />

      <nav
        className="flex items-center justify-between bg-white/80 backdrop-blur supports-[backdrop-filter]:bg-white/60
                   px-3 md:px-4 py-2.5 shadow-sm border-b"
      >
        {/* Left: Brand + current section */}
        <div className="flex items-center gap-3 min-w-0 ml-14">
          <div className="min-w-0">
            <div className="font-extrabold text-lg tracking-tight text-gray-900">
              Admin Dashboard
            </div>
            <div className="text-[12px] text-gray-500 truncate">
              {pathname.replace("/", "") || "home"}
            </div>
          </div>
        </div>

        {/* Center: Quick links (desktop) */}
        {/* <div className="hidden md:flex items-center gap-1">
          {topLinks.map((l) => (
            <ActiveLink key={l.to} to={l.to}>
              {l.label}
            </ActiveLink>
          ))}
        </div> */}

        {/* Right: Search + actions */}
        <div className="flex items-center gap-2">
          {/* mini search */}
          <div className="hidden sm:flex items-center px-2 py-1.5 rounded-lg border bg-white">
            <svg
              viewBox="0 0 24 24"
              className="w-4 h-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <circle cx="11" cy="11" r="7" />
              <path d="M21 21l-3.6-3.6" />
            </svg>
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Quick search..."
              className="ml-2 w-40 md:w-56 text-sm outline-none placeholder:text-gray-400"
            />
          </div>

          {/* notifications */}
          <div className="relative">
            <button
              ref={notifBtnRef}
              onClick={() => setNotifOpen((v) => !v)}
              className="relative p-2 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-200"
              title="Notifications"
              aria-label="Notifications"
            >
              <svg
                viewBox="0 0 24 24"
                className="w-5 h-5 text-gray-700"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M15 17h5l-1.4-1.4A2 2 0 0 1 18 14.2V11a6 6 0 1 0-12 0v3.2a2 2 0 0 1-.6 1.4L4 17h5" />
                <path d="M9 17a3 3 0 0 0 6 0" />
              </svg>
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] grid place-items-center">
                  {unreadCount > 9 ? "9+" : unreadCount}
                </span>
              )}
            </button>

            {notifOpen && (
              <div className="absolute right-0 mt-2 w-[360px] max-w-[90vw] rounded-xl border bg-white shadow-lg overflow-hidden z-50">
                <div className="px-3 py-2 text-xs text-gray-500 border-b flex justify-between">
                  <span>Notifications</span>
                  <span className="text-gray-400">{notifs.length} items</span>
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {notifs.length === 0 ? (
                    <div className="px-4 py-6 text-sm text-gray-500">
                      No notifications
                    </div>
                  ) : (
                    notifs.map((n) => (
                      <button
                        key={n.id}
                        onClick={() => {
                          markNotifRead(n.id);
                          // Optionally deep-link:
                          // window.location.href = `/admin/${n.resourceType}s/${n.resourceId}`;
                        }}
                        className={
                          "w-full text-left px-4 py-3 flex items-start gap-3 hover:bg-gray-50 border-b last:border-b-0 " +
                          (n.status !== "read" ? "bg-blue-50/40" : "")
                        }
                      >
                        <div className="mt-1">
                          {n.action === "created" ? "🆕" : "✏️"}
                        </div>
                        <div className="min-w-0">
                          <div className="text-sm font-semibold text-gray-800 truncate">
                            {n.title || "(untitled)"} — {n.resourceType}
                          </div>
                          <div className="text-xs text-gray-500">
                            {n.action === "created"
                              ? "New item submitted"
                              : "Updated — needs review"}
                          </div>
                        </div>
                        {n.status !== "read" && (
                          <span className="ml-auto mt-1 h-2 w-2 rounded-full bg-blue-600" />
                        )}
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          {/* user menu */}
          <div className="relative">
            <button
              ref={userBtnRef}
              onClick={() => setUserOpen((v) => !v)}
              className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-xl hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-200"
              aria-haspopup="menu"
              aria-expanded={userOpen}
              title="Account"
            >
              <Avatar name="Admin" /* photoURL={currentUser?.photoURL} */ />
              <span className="hidden sm:block text-sm font-medium text-gray-800">
                Admin
              </span>
              <svg
                viewBox="0 0 24 24"
                className="w-4 h-4 text-gray-500"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>

            {userOpen && (
              <div
                role="menu"
                className="absolute right-0 mt-2 w-56 rounded-xl border bg-white shadow-lg overflow-hidden z-50"
              >
                <div className="px-3 py-2 text-xs text-gray-500 border-b">
                  Signed in as <b>admin@yourapp.com</b>
                </div>
                <div className="py-1">
                  <NavLink
                    to="/dashboard"
                    className="block px-3 py-2 text-sm hover:bg-gray-50"
                    onClick={() => setUserOpen(false)}
                  >
                    Profile
                  </NavLink>
                  <NavLink
                    to="/settings"
                    className="block px-3 py-2 text-sm hover:bg-gray-50"
                    onClick={() => setUserOpen(false)}
                  >
                    Settings
                  </NavLink>
                </div>
                <div className="border-t">
                  <button
                    onClick={() => {
                      setUserOpen(false);
                      // hook your sign-out here
                    }}
                    className="w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50"
                  >
                    Sign out
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* mobile menu button */}
          <button
            className="md:hidden p-2 rounded-lg hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-200"
            onClick={() => setMenuOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            <svg
              viewBox="0 0 24 24"
              className="w-5 h-5 text-gray-700"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M3 6h18M3 12h18M3 18h18" />
            </svg>
          </button>
        </div>
      </nav>

      {/* Mobile slide-down menu */}
      {menuOpen && (
        <div className="md:hidden bg-white border-b shadow-sm">
          <div className="px-3 py-2 flex flex-col gap-1">
            {topLinks.map((l) => (
              <ActiveLink key={l.to} to={l.to}>
                {l.label}
              </ActiveLink>
            ))}
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
